import type * as React from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface DataTableHeader {
  label: string;
  className?: string;
}

interface DataTableProps {
  headers: DataTableHeader[];
  children: React.ReactNode;
  loading?: boolean;
  empty?: React.ReactNode;
  colSpan?: number;
  className?: string;
}

export function DataTable({
  headers,
  children,
  loading = false,
  empty,
  colSpan,
  className,
}: DataTableProps) {
  const span = colSpan ?? headers.length;
  const hasChildren = Array.isArray(children)
    ? children.filter(Boolean).length > 0
    : Boolean(children);
  const showEmpty = !loading && !hasChildren && empty;

  return (
    <div className={cn("overflow-x-auto rounded-lg border", className)}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            {headers.map((header) => (
              <th
                key={header.label}
                className={cn(
                  "px-4 py-3 text-left text-xs font-medium text-muted-foreground",
                  header.className,
                )}
              >
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 3 }).map((_, rowIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows never reorder
              <tr key={`skeleton-${rowIndex}`} className="border-b last:border-b-0">
                {headers.map((_header, colIndex) => (
                  <td
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton cells never reorder
                    key={`skeleton-${rowIndex}-${colIndex}`}
                    className="px-4 py-3"
                  >
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))
          ) : showEmpty ? (
            <tr>
              <td colSpan={span}>{empty}</td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}
